import { SwapCalls, ArrowBack } from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';
import { URLPaths } from 'models/enums';
import ssImage from 'assets/images/svg/single-send-preview.svg';
import tdImage from 'assets/images/svg/time-delay-preview.svg';
import csImage from 'assets/images/svg/conditional-split-preview.svg';
import { CampaignChannels, CampaignKey } from 'domains/campaigns/types';
import { getCampaignRoute } from 'domains/campaigns/state/utils';
import Button from 'domains/core/components/Button';
import UpdateTag from 'domains/core/components/UpdateTag';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { channelIcons } from '../../CurationsPage/utils';
import CurationItem from './CurationItem';

// Large SVG loaded as external asset to avoid BABEL processing
const customImage = '/assets/images/svg/custom-curation-preview.svg';

// Styled component for channel icons
const StyledChannelIcon = {
    fontSize: 101,
    color: 'action.active',
    paddingTop: '36px',
};

const gridSizes = { xs: 12, md: 6, xl: 3 };
const accessControlGridSizes = { xs: 12, sm: 6, md: 3 };

const CurationLandingPage = () => {
    const history = useHistory();
    const { accessControlLayer: accessControlLayerFlag } = useFlags();
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();

    const hasDirectMailCurations = isFeatureSettingsSuccess && featureSettings?.directmail_enabled;
    const hasSMSCurations = isFeatureSettingsSuccess && featureSettings?.sms_enabled;
    const hasVoiceCurations = isFeatureSettingsSuccess && featureSettings.voice_enabled;
    const hasAccessControlLayer = isFeatureSettingsSuccess && featureSettings?.acl_enabled && accessControlLayerFlag;

    const routeSingleSend = getCampaignRoute({ campaignKey: CampaignKey.SINGLE_SEND });
    const routeEmailSeries = getCampaignRoute({ campaignKey: CampaignKey.EMAIL_SERIES });

    const singleSendImage = <img src={ssImage} alt="" width="100%" />;
    const timeDelayImage = <img src={tdImage} alt="" width="100%" />;
    const conditionalSplitImage = <img src={csImage} alt="" width="100%" />;
    const customCurationImage = <img src={customImage} alt="" width="100%" />;

    const renderSeparateChannelGrids = () => {
        const EmailIcon = channelIcons[CampaignChannels.EMAIL];
        const SMSIcon = channelIcons[CampaignChannels.SMS];
        const VoiceIcon = channelIcons[CampaignChannels.VOICE_CALL];
        const DirectMailIcon = channelIcons[CampaignChannels.DIRECT_MAIL];

        return (
            <>
                {/* Email Grid */}
                <Grid item {...accessControlGridSizes}>
                    <CurationItem
                        imageSrc={
                            <Stack alignItems="center" justifyContent="center">
                                <EmailIcon sx={StyledChannelIcon} />
                            </Stack>
                        }
                        title="Email"
                        isAccessControlLayer={hasAccessControlLayer}
                        onClick={() =>
                            history.push({
                                pathname: URLPaths.CURATIONS_CUSTOM,
                                state: { channel: CampaignChannels.EMAIL },
                            })
                        }
                    />
                </Grid>

                {/* SMS Grid */}
                {hasSMSCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <SMSIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Text Message"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() =>
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.SMS },
                                })
                            }
                        />
                    </Grid>
                )}

                {/* Voice Grid */}
                {hasVoiceCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <VoiceIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Voice Call"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() =>
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.VOICE_CALL },
                                })
                            }
                        />
                    </Grid>
                )}

                {/* Direct Mail Grid */}
                {hasDirectMailCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <DirectMailIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Direct Mail"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() =>
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.DIRECT_MAIL },
                                })
                            }
                        />
                    </Grid>
                )}
            </>
        );
    };

    const renderCustomCurationOption = () => {
        const sharedProps = {
            imageSrc: customCurationImage,
            subtitle: 'Build your own communication flows',
            title: 'Custom Curation',
            updateTag: <UpdateTag tag="early release" tagSx={{ margin: 2 }} />,
        };
        const navigateToCustomCurationsPage = () =>
            history.push({
                pathname: URLPaths.CURATIONS_CUSTOM,
                state: { channel: CampaignChannels.EMAIL },
            });

        const shouldRenderCustomOptions = hasDirectMailCurations || hasVoiceCurations || hasSMSCurations;

        if (shouldRenderCustomOptions) {
            return (
                <Grid item {...gridSizes}>
                    <CurationItem
                        options={[
                            {
                                label: 'Email',
                                onClick: navigateToCustomCurationsPage,
                            },
                            ...(hasSMSCurations
                                ? [
                                      {
                                          label: 'Text Message',
                                          onClick: () =>
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.SMS },
                                              }),
                                      },
                                  ]
                                : []),
                            ...(hasVoiceCurations
                                ? [
                                      {
                                          label: 'Voice Call',
                                          onClick: () =>
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.VOICE_CALL },
                                              }),
                                      },
                                  ]
                                : []),
                            ...(hasDirectMailCurations
                                ? [
                                      {
                                          label: 'Direct Mail',
                                          onClick: () =>
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.DIRECT_MAIL },
                                              }),
                                      },
                                  ]
                                : []),
                        ]}
                        {...sharedProps}
                    />
                </Grid>
            );
        }

        return (
            <Grid item {...gridSizes}>
                <CurationItem onClick={navigateToCustomCurationsPage} {...sharedProps} />
            </Grid>
        );
    };

    return (
        <>
            <Grid container direction="column" my={8}>
                <Grid container direction="column">
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" columnGap={4}>
                            <Stack direction="row" alignItems="end" columnGap={0.5}>
                                <SwapCalls fontSize="medium" color="action" />
                                <Typography variant="caption" color="primary.main">
                                    curations
                                </Typography>
                            </Stack>
                        </Stack>
                        <Button
                            variant="text"
                            startIcon={<ArrowBack />}
                            onClick={() => history.push(URLPaths.CURATIONS)}
                        >
                            Back
                        </Button>
                    </Stack>
                    <Typography variant="h3" mb={2}>
                        create better connections
                    </Typography>
                </Grid>
                <Grid container>
                    <Grid item>
                        <Typography>
                            Select a Curation below to easily reach your customers at the right time with the right
                            content.
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={4} pb={4}>
                {hasAccessControlLayer ? (
                    // Show separate channel grids when access control layer is enabled
                    renderSeparateChannelGrids()
                ) : (
                    // Show current structure when access control layer is disabled
                    <>
                        {renderCustomCurationOption()}
                        <Grid item {...gridSizes}>
                            <CurationItem
                                imageSrc={singleSendImage}
                                subtitle="Quick and direct communication with individuals"
                                title="Single Send"
                                onClick={() => history.push(routeSingleSend)}
                            />
                        </Grid>
                        <Grid item {...gridSizes}>
                            <CurationItem
                                imageSrc={timeDelayImage}
                                subtitle="Connected sends to encourage engagement"
                                title="Email Series"
                                onClick={() => history.push(routeEmailSeries)}
                            />
                        </Grid>
                        <Grid item {...gridSizes}>
                            <CurationItem
                                imageSrc={conditionalSplitImage}
                                subtitle="Focus communication based on engagement"
                                title="Conditional Split"
                                onClick={() => history.push(URLPaths.CURATIONS_CONDITIONAL_SPLIT)}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );
};

export default CurationLandingPage;
